import React, { useId, useState } from "react";
import { PublicBaseSelectProps } from "react-select/base";
import {
  useCustomReactSelectComponents,
  useValueContainer,
} from "./use_custom_react_select_components";
import classNames from "classnames";
import { buildTheme, buildStyles } from "../styles";
import type { Grouped, Option } from "../types";
import type { MultiValue, SelectComponentsConfig, SingleValue } from "react-select";

export type SharedReactSelectProps = Omit<
  PublicBaseSelectProps<Option, boolean, Grouped<Option>>,
  "inputValue" | "onInputChange" | "onMenuOpen" | "onMenuClose" | "value"
> & {
  "aria-describedby"?: string;
  id: string;
  isValidNewOption: (
    inputValue: string,
    value: Readonly<Option[]>,
    options: Readonly<Option[] | Grouped<Option>[]>,
  ) => boolean;
};

const EmptyComponent = () => null;

interface SharedReactSelectPropsOptions {
  error?: string | null;
  label?: string | null;
  hideDropdownIndicator?: boolean;
  hideNoOptionsMessage?: boolean;
  portalMenu?: boolean;
  startIcon?: React.ReactNode;
  menuWidth?: string | number;
}

const isValidNewOption = (
  inputValue: string,
  selectedOptions: Readonly<Option[]>,
  options: Readonly<Option[] | Grouped<Option>[]>,
): boolean => {
  if (options.length === 0) {
    return true;
  }

  const optionSet = new Set(
    options.flatMap((option) => {
      if (!option) {
        return [];
      }

      if ((option as Grouped<Option>).options) {
        return (option as Grouped<Option>).options.map((nestedOption) => nestedOption.value);
      }

      return option.value;
    }),
  );

  for (const selectedOption of selectedOptions) {
    const value = selectedOption.value;

    if (!optionSet.has(value)) {
      return false;
    }
  }

  return inputValue.trim().length > 0;
};

export const useSharedReactSelectProps = (
  onChange: (value: MultiValue<Option> | SingleValue<Option>) => void,
  options?: SharedReactSelectPropsOptions,
): SharedReactSelectProps => {
  const hasError = !!options?.error;
  const hasLabel = !!options?.label;
  const hasStartIcon = !!options?.startIcon;

  const id = useId();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const ValueContainer = useValueContainer(options?.startIcon);

  const componentOverrides: SelectComponentsConfig<Option, boolean, Grouped<Option>> = {
    ValueContainer,
  };

  if (options?.hideDropdownIndicator) {
    componentOverrides.DropdownIndicator = EmptyComponent;
  }

  if (options?.hideNoOptionsMessage) {
    componentOverrides.NoOptionsMessage = EmptyComponent;
  }

  const components = useCustomReactSelectComponents(componentOverrides);

  const onKeyUp = (key: React.KeyboardEvent) => {
    switch (key.code) {
      case "Escape":
      case "Tab":
        return setMenuIsOpen(false);
      case "Enter":
      case "ShiftLeft":
      case "ShiftRight":
        return;
      default:
        return setMenuIsOpen(true);
    }
  };

  const handleChange = (value: MultiValue<Option> | SingleValue<Option>) => {
    onChange(value);
    setMenuIsOpen(false);
  };

  return {
    "aria-labelledby": `${id}-label`,
    "aria-describedby": `${id}-description ${id}-error`,
    "aria-invalid": hasError,
    "aria-errormessage": `${id}-error`,
    classNamePrefix: "select",
    classNames: {
      control: () =>
        classNames({
          "select__control--error": hasError,
          "select__control--no-label": !hasLabel,
        }),
      valueContainer: () =>
        classNames({
          "select__start-icon": hasStartIcon,
        }),
    },
    components,
    id,
    isValidNewOption,
    menuIsOpen,
    ...((options?.portalMenu ?? true) && { menuPortalTarget: document.body }),
    onBlur: () => setMenuIsOpen(false),
    onChange: handleChange,
    onKeyUp,
    onMouseUp: () => setMenuIsOpen(!menuIsOpen),
    onTouchEnd: () => setMenuIsOpen(!menuIsOpen),
    openMenuOnClick: false,
    styles: buildStyles({ menuWidth: options?.menuWidth }),
    theme: buildTheme,
  };
};
